import React, { FunctionComponent, useState, useEffect } from 'react';

import { FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { OutsideClick } from 'common/outside-click/OutsideClick';
import { Overlay } from 'core/overlay/Overlay';

import 'common/input-search-dropdown/InputSearchDropdown.scss';

const InputSearchDropdownOverlayArea = 'input-search-dropdown-overlay-area';
const DEBOUNCE_TIME = 500;

export const OPTIONAL_DATA_KEY = '_data';

export interface OptionData {
    /** text to be displayed in the search dropdown */
    displayName: string;
    [OPTIONAL_DATA_KEY]?: any;
}

interface DropdownProps {
    options: OptionData[] | null;
}

interface Props {
    getDropdownData?: (inputValue: string) => Promise<OptionData[]>;
    DropdownMenuCustomComponent?: FunctionComponent<any>;
    onDropdownOptionSelect: (option: OptionData) => void;
    value: string;
    placeholder?: string;
    onChange?: (inputValue: string) => void;
}

export const InputSearchDropdown: FunctionComponent<Props> = ({
    getDropdownData,
    DropdownMenuCustomComponent,
    onDropdownOptionSelect,
    value,
    placeholder,
    onChange,
}) => {
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const [inputValue, setInputValue] = useState<string>(value);
    const [options, setOptions] = useState<OptionData[] | null>(null);
    const { t } = useTranslation('common');
    const [isDropdownVisible, setIsDropdownVisible] = useState(true);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const debounce = (inputValue: string): void => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            setDebounceTimer(null);
        }

        setDebounceTimer(
            setTimeout(async () => {
                if (!inputValue) {
                    setOptions(null);
                }

                if (inputValue && getDropdownData) {
                    const response: OptionData[] = await trackPromise(
                        getDropdownData(inputValue),
                        `${InputSearchDropdownOverlayArea}`,
                    );

                    setOptions(response);
                }
            }, DEBOUNCE_TIME),
        );
    };

    const Dropdown: FunctionComponent<DropdownProps> = ({ options }): JSX.Element | null => {
        if (options && options.length > 0 && isDropdownVisible) {
            return (
                <div className="outputs">
                    {options.map((option: OptionData, i: number) => {
                        return (
                            <div
                                role="presentation"
                                key={`input-search-dropdown-output-${i}`} // eslint-disable-line
                                className="outputs-list-item"
                                onClick={(): void => {
                                    setInputValue(option.displayName);
                                    onDropdownOptionSelect(option);
                                    setOptions(null);
                                }}
                            >
                                {DropdownMenuCustomComponent ? (
                                    <DropdownMenuCustomComponent additionalData={option} inputValue={inputValue} />
                                ) : (
                                    <span>{option.displayName}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (options && !options.length && isDropdownVisible) {
            return <div className="outputs px-4 py-2 w-25">{t('NO_DATA')}</div>;
        }

        return null;
    };

    return (
        <OutsideClick
            outsideClickHandler={() => {
                setIsDropdownVisible(false);
            }}
            styles={{ width: '100%' }}
        >
            <div className="r4e-input-search w-100">
                <FormControl
                    placeholder={placeholder}
                    className="input-container"
                    value={inputValue || ''}
                    onChange={({ target: { value } }): void => {
                        const trimmedValue = value.replace(/^\s+/g, ''); // Trim leading/trailing spaces

                        setInputValue(trimmedValue);
                        if (onChange) onChange(trimmedValue);
                        if (getDropdownData) debounce(trimmedValue);
                    }}
                    onClick={() => {
                        setIsDropdownVisible(true);
                    }}
                />
                {getDropdownData ? (
                    <>
                        <div className="loading">
                            <Overlay area={`${InputSearchDropdownOverlayArea}`} />
                        </div>
                        <Dropdown options={options} />
                    </>
                ) : null}
            </div>
        </OutsideClick>
    );
};
